import React from 'react';
import { X } from 'lucide-react';

interface PartCreateFlowPreviewProps {
  onClose: () => void;
}

const STEPS = [
  {
    label: 'Upload',
    description: 'Select document from Google Drive or upload from device',
    icon: '📄',
    color: 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700',
    detail: 'Supports PDF, PNG, JPG',
  },
  {
    label: 'Preview',
    description: 'View document & draw extraction regions',
    icon: '🔍',
    color: 'bg-purple-100 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700',
    detail: 'Draw polygon boxes to target fields',
  },
  {
    label: 'Extract',
    description: 'Claude Vision extracts fields from regions',
    icon: '🤖',
    color: 'bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700',
    detail: 'Region images sent as base64 PNG',
  },
  {
    label: 'Review',
    description: 'Review & edit extracted part data (split view)',
    icon: '✏️',
    color: 'bg-teal-100 dark:bg-teal-900/30 border-teal-300 dark:border-teal-700',
    detail: 'AI-filled fields are editable before save',
  },
  {
    label: 'Save',
    description: 'Write part code to Google Sheets',
    icon: '💾',
    color: 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700',
    detail: 'Auto-generates part number by category',
  },
];

const UPLOAD_SOURCES = [
  {
    name: 'Google Drive',
    description: 'Browse and select files from company Drive',
    icon: (
      <svg className="w-5 h-5 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
        <path d="M7.71 3.5L1.15 15l3.43 5.97L11.14 9.47 7.71 3.5zm1.14 0h6.86l6.58 11.5h-6.86L8.85 3.5zM19.57 15H5.71l-3.43 6h13.86l3.43-6z" />
      </svg>
    ),
  },
  {
    name: 'Upload from Device',
    description: 'Select local PDF or image file',
    icon: (
      <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
      </svg>
    ),
  },
];

const EXTRACTION_FIELDS = [
  { name: '廠牌 (Brand)', source: 'AI-extracted' },
  { name: '廠商品名 (Vendor Product)', source: 'AI-extracted' },
  { name: '廠商規格 (Vendor Spec)', source: 'AI-extracted' },
  { name: '類別 (Category)', source: 'AI-extracted / Manual' },
  { name: '尺寸 (Dimension)', source: 'AI-extracted' },
  { name: 'Footprint', source: 'AI-extracted (electronic parts only)' },
];

const PartCreateFlowPreview: React.FC<PartCreateFlowPreviewProps> = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Part Create Flow
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Preset flow — View only
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Flow Steps */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Workflow Steps</h3>
            <div className="space-y-1">
              {STEPS.map((step, i) => (
                <div key={step.label} className="flex items-start gap-3">
                  {/* Connector line */}
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-lg border ${step.color} flex items-center justify-center text-sm`}>
                      {step.icon}
                    </div>
                    {i < STEPS.length - 1 && (
                      <div className="w-0.5 h-6 bg-gray-200 dark:bg-gray-600" />
                    )}
                  </div>
                  <div className="pt-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{step.label}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{step.description}</p>
                    {step.detail && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{step.detail}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Upload Sources */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Upload Sources</h3>
            <div className="grid grid-cols-2 gap-3">
              {UPLOAD_SOURCES.map((src) => (
                <div
                  key={src.name}
                  className="flex items-start gap-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-3"
                >
                  <div className="mt-0.5">{src.icon}</div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{src.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{src.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Extraction Fields */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Extraction Fields</h3>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">Field</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {EXTRACTION_FIELDS.map((field) => (
                    <tr key={field.name} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{field.name}</td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{field.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Config Defaults */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Default Configuration</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg px-3 py-2">
                <span className="text-gray-500 dark:text-gray-400">Category:</span>{' '}
                <span className="text-gray-900 dark:text-gray-100">Electronic Parts</span>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg px-3 py-2">
                <span className="text-gray-500 dark:text-gray-400">Part Type:</span>{' '}
                <span className="text-gray-900 dark:text-gray-100">R (Resistor)</span>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg px-3 py-2">
                <span className="text-gray-500 dark:text-gray-400">Auto-gen PN:</span>{' '}
                <span className="text-emerald-600 dark:text-emerald-400">Yes</span>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg px-3 py-2">
                <span className="text-gray-500 dark:text-gray-400">DigiKey Lookup:</span>{' '}
                <span className="text-gray-400">No</span>
              </div>
            </div>
          </div>

          {/* Region Selection Info */}
          <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-3">
            <h4 className="text-sm font-medium text-indigo-800 dark:text-indigo-300 mb-1">Region-Based Extraction</h4>
            <p className="text-xs text-indigo-600 dark:text-indigo-400">
              In the preview step, draw polygon boxes on the PDF to target specific areas for field extraction.
              Regions use percentage-based coordinates for resolution independence.
              Each region is cropped client-side and sent as a base64 image to Claude Vision.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PartCreateFlowPreview;
